import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import StarRating from "../components/ui/StarRating";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import {
  SERVICE_ORDER_ATTACHMENT_ACCEPT,
  validateServiceOrderAttachment,
} from "../utils/serviceOrderAttachments.js";
import {
  createServiceOrderMilestone,
  formatServiceCurrency,
  getFreelancerServiceTitle,
  getMyFreelancerOrders,
  getServiceOrderDetails,
  updateServiceOrderStatus,
  getOrderMessages,
  sendOrderMessage,
  findRecoveredOrderMessage,
  markOrderMessagesRead,
  deliverServiceOrder,
  respondOrderCancellation,
} from "../api/freelancerServices";

function getStatusMeta(status, isAr) {
  const map = {
    pending: { className: "text-bg-warning", label: isAr ? "قيد الانتظار" : "Pending" },
    paid: { className: "text-bg-primary", label: isAr ? "مدفوع" : "Paid" },
    in_progress: { className: "text-bg-info", label: isAr ? "قيد التنفيذ" : "In Progress" },
    delivered: { className: "text-bg-success", label: isAr ? "تم التسليم" : "Delivered" },
    completed: { className: "text-bg-success", label: isAr ? "مكتمل" : "Completed" },
    cancelled: { className: "text-bg-danger", label: isAr ? "ملغي" : "Cancelled" },
    disputed: { className: "text-bg-danger", label: isAr ? "نزاع" : "Disputed" },
    revision_requested: { className: "text-bg-warning", label: isAr ? "طلب مراجعة" : "Revision Requested" },
    cancellation_requested: { className: "text-bg-warning", label: isAr ? "طلب إلغاء" : "Cancellation Requested" },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "text-bg-secondary",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

function getNextActions(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "paid") return ["in_progress"];
  if (normalized === "in_progress") return [];
  if (normalized === "delivered") return ["completed"];
  if (normalized === "revision_requested") return ["in_progress"];
  return [];
}

function getStorageBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL || "/laravel-api/api";
  return apiUrl.replace(/\/api(\/v1)?$/, "");
}

function createMilestoneDraft() {
  return {
    title: "",
    description: "",
    amount: "",
    dueAt: "",
  };
}

function canManageMilestones(order) {
  const status = String(order?.status || "").toLowerCase();
  const paymentStatus = String(order?.payment_status || order?.paymentStatus || "").toLowerCase();
  const hasActiveDispute = Array.isArray(order?.disputes)
    && order.disputes.some((dispute) => ["open", "under_review"].includes(String(dispute?.status || "").toLowerCase()));

  if (paymentStatus !== "paid") {
    return false;
  }

  if (hasActiveDispute) {
    return false;
  }

  return ["paid", "in_progress", "delivered"].includes(status);
}

export default function MyFreelancerOrders() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const { orderId: orderIdParam } = useParams();
  const orderId = orderIdParam ? Number(orderIdParam) : null;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionOrderId, setActionOrderId] = useState(0);
  const [expandedOrderId, setExpandedOrderId] = useState(0);
  const [detailLoadingId, setDetailLoadingId] = useState(0);
  const [milestoneDrafts, setMilestoneDrafts] = useState({});
  const [deliveryDrafts, setDeliveryDrafts] = useState({});
  const [orderMessages, setOrderMessages] = useState({});
  const [messageTexts, setMessageTexts] = useState({});
  const [messageFiles, setMessageFiles] = useState({});
  const [messageFileErrors, setMessageFileErrors] = useState({});

  function handleMessageFileChange(orderId, file, input) {
    if (!file) {
      setMessageFiles((current) => ({ ...current, [orderId]: null }));
      setMessageFileErrors((current) => ({ ...current, [orderId]: "" }));
      return;
    }

    const validationError = validateServiceOrderAttachment(file);
    if (validationError) {
      setMessageFiles((current) => ({ ...current, [orderId]: null }));
      setMessageFileErrors((current) => ({ ...current, [orderId]: validationError }));
      if (input) {
        input.value = "";
      }
      return;
    }

    setMessageFiles((current) => ({ ...current, [orderId]: file }));
    setMessageFileErrors((current) => ({ ...current, [orderId]: "" }));
  }

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        let data;
        if (orderId) {
          // Load specific order
          const orderDetails = await getServiceOrderDetails(orderId);
          data = [orderDetails];
          if (active) {
            setExpandedOrderId(Number(orderId));

            // Load messages for this order
            try {
              const messages = await getOrderMessages(orderId);
              setOrderMessages((current) => ({ ...current, [orderId]: messages }));
              await markOrderMessagesRead(orderId);
            } catch (msgErr) {
              logger.error("Failed to load messages", msgErr);
            }
          }
        } else {
          // Load all orders
          data = await getMyFreelancerOrders();
        }

        if (active) {
          setOrders(data);
        }
      } catch (err) {
        logger.error("My freelancer orders error", err);
        if (active) {
          setError(isAr ? "تعذر تحميل طلباتك كمستقل." : "Unable to load your freelancer orders.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [isAr, orderId]);

  function updateOrderState(orderId, updater) {
    setOrders((current) =>
      current.map((order) => (order.id === orderId ? updater(order) : order)),
    );
  }

  async function handleToggleDetails(orderId) {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(0);
      return;
    }

    try {
      setDetailLoadingId(orderId);
      setError("");
      const details = await getServiceOrderDetails(orderId);
      updateOrderState(orderId, () => details);
      setExpandedOrderId(orderId);

      try {
        const messages = await getOrderMessages(orderId);
        setOrderMessages((current) => ({ ...current, [orderId]: messages }));
        await markOrderMessagesRead(orderId);
      } catch (msgErr) {
        logger.error("Failed to load messages", msgErr);
      }
    } catch (err) {
      logger.error("Freelancer order details error", err);
      setError(err?.message || (isAr ? "تعذر تحميل التفاصيل." : "Unable to load the order details."));
    } finally {
      setDetailLoadingId(0);
    }
  }

  async function handleStatusUpdate(orderId, status) {
    try {
      setActionOrderId(orderId);
      setError("");
      setNotice("");

      const result = await updateServiceOrderStatus(orderId, { status });
      const nextOrder = result?.data || null;

      if (nextOrder) {
        updateOrderState(orderId, (currentOrder) => ({
          ...currentOrder,
          ...nextOrder,
          milestones: Array.isArray(nextOrder.milestones) ? nextOrder.milestones : currentOrder?.milestones || [],
          disputes: Array.isArray(nextOrder.disputes) ? nextOrder.disputes : currentOrder?.disputes || [],
          refunds: Array.isArray(nextOrder.refunds) ? nextOrder.refunds : currentOrder?.refunds || [],
          review: nextOrder.review ?? currentOrder?.review ?? null,
          payment_summary: nextOrder.payment_summary ?? currentOrder?.payment_summary ?? null,
        }));
      }

      setNotice(
        result?.message ||
          (isAr ? "تم تحديث حالة الطلب بنجاح." : "Service order status updated successfully.")
      );
    } catch (err) {
      logger.error("Freelancer order status update error", err);
      setError(err?.message || (isAr ? "تعذر تحديث الحالة." : "Unable to update the status."));
    } finally {
      setActionOrderId(0);
    }
  }

  function updateMilestoneDraft(orderId, field, value) {
    setMilestoneDrafts((current) => ({
      ...current,
      [orderId]: {
        ...(current[orderId] || createMilestoneDraft()),
        [field]: value,
      },
    }));
  }

  async function handleCreateMilestone(orderId) {
    const draft = milestoneDrafts[orderId] || createMilestoneDraft();

    try {
      setActionOrderId(orderId);
      setError("");
      setNotice("");

      const milestone = await createServiceOrderMilestone(orderId, {
        title: draft.title.trim(),
        description: draft.description.trim() || undefined,
        amount: Number(draft.amount),
        due_at: draft.dueAt || undefined,
      });

      updateOrderState(orderId, (order) => ({
        ...order,
        milestones: [...(Array.isArray(order.milestones) ? order.milestones : []), milestone],
      }));
      setMilestoneDrafts((current) => ({ ...current, [orderId]: createMilestoneDraft() }));
      setNotice(isAr ? "تمت إضافة المعلم بنجاح." : "Milestone created successfully.");
    } catch (err) {
      logger.error("Create milestone error", err);
      setError(err?.message || (isAr ? "تعذر إنشاء المعلم." : "Unable to create the milestone."));
    } finally {
      setActionOrderId(0);
    }
  }

  async function handleDeliver(orderId) {
    const draft = deliveryDrafts[orderId] || {};
    if (!draft.files || draft.files.length === 0) return;

    try {
      setActionOrderId(orderId);
      setError("");
      setNotice("");

      const formData = new FormData();
      draft.files.forEach((file) => formData.append("files[]", file));
      if (draft.notes?.trim()) {
        formData.append("delivery_notes", draft.notes);
      }

      const result = await deliverServiceOrder(orderId, formData);
      const nextOrder = result?.data || result || null;

      if (nextOrder) {
        updateOrderState(orderId, (currentOrder) => ({
          ...currentOrder,
          ...nextOrder,
          status: nextOrder.status || currentOrder.status,
          delivery_files: nextOrder.delivery_files || currentOrder.delivery_files,
        }));
      }

      setDeliveryDrafts((current) => ({ ...current, [orderId]: {} }));
      setNotice(isAr ? "تم تسليم الملفات بنجاح." : "Delivery submitted successfully.");

      // Trigger navbar to refresh notifications
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Deliver order error", err);
      setError(err?.message || (isAr ? "تعذر تسليم الملفات." : "Unable to submit delivery."));
    } finally {
      setActionOrderId(0);
    }
  }

  async function handleSendMessage(orderId) {
    // Prevent duplicate submissions
    if (actionOrderId === orderId) return;

    const text = (messageTexts[orderId] || "").trim();
    if (!text && !messageFiles[orderId]) return;
    const previousMessages = Array.isArray(orderMessages[orderId]) ? orderMessages[orderId] : [];
    const hadAttachment = Boolean(messageFiles[orderId]);

    try {
      setActionOrderId(orderId);
      setError("");

      const formData = new FormData();
      formData.append("message", text);
      if (messageFiles[orderId]) {
        formData.append("attachment", messageFiles[orderId]);
      }

      const newMessage = await sendOrderMessage(orderId, formData);
      setOrderMessages((current) => ({
        ...current,
        [orderId]: [newMessage, ...(current[orderId] || [])],
      }));
      setMessageTexts((current) => ({ ...current, [orderId]: "" }));
      setMessageFiles((current) => ({ ...current, [orderId]: null }));
      setMessageFileErrors((current) => ({ ...current, [orderId]: "" }));

      // Trigger navbar to refresh notifications
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Send message error", err);
      try {
        const latestMessages = await getOrderMessages(orderId);
        const recoveredMessage = findRecoveredOrderMessage(previousMessages, latestMessages, text, hadAttachment);

        if (recoveredMessage) {
          setOrderMessages((current) => ({
            ...current,
            [orderId]: latestMessages,
          }));
          setMessageTexts((current) => ({ ...current, [orderId]: "" }));
          setMessageFiles((current) => ({ ...current, [orderId]: null }));
          setMessageFileErrors((current) => ({ ...current, [orderId]: "" }));
          window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
          return;
        }
      } catch (refreshErr) {
        logger.error("Refresh messages after send error failed", refreshErr);
      }
      setError(err?.message || (isAr ? "تعذر إرسال الرسالة." : "Unable to send message."));
    } finally {
      setActionOrderId(0);
    }
  }

  async function handleRespondCancellation(orderId, action) {
    try {
      setActionOrderId(orderId);
      setError("");
      setNotice("");

      const result = await respondOrderCancellation(orderId, { action });
      const nextOrder = result?.data || result || null;

      if (nextOrder) {
        updateOrderState(orderId, (currentOrder) => ({
          ...currentOrder,
          ...nextOrder,
          status: nextOrder.status || currentOrder.status,
        }));
      }

      setNotice(
        result?.message ||
          (action === "approve"
            ? isAr
              ? "تم قبول طلب الإلغاء."
              : "Cancellation approved."
            : isAr
              ? "تم رفض طلب الإلغاء."
              : "Cancellation rejected.")
      );

      // Trigger navbar to refresh notifications
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Respond cancellation error", err);
      setError(err?.message || (isAr ? "تعذر معالجة طلب الإلغاء." : "Unable to respond to cancellation."));
    } finally {
      setActionOrderId(0);
    }
  }

  return (
    <div className="container py-4 py-lg-5" dir={isAr ? "rtl" : "ltr"}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h1 className="fw-bold mb-1">{isAr ? "طلباتي كمستقل" : "My Freelancer Orders"}</h1>
          <p className="text-muted mb-0">
            {isAr
              ? "أدر التنفيذ والمعالم وحالة الطلب والأرباح المحجوزة من مكان واحد."
              : "Manage fulfillment, milestones, order status, and escrowed earnings from one place."}
          </p>
        </div>
        <Link to="/freelancers" className="btn btn-outline-dark rounded-pill px-4">
          {isAr ? "سوق المستقلين" : "Marketplace"}
        </Link>
      </div>

      {notice ? <div className="alert alert-success rounded-4">{notice}</div> : null}

      {loading ? (
        <div className="py-5 text-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p className="mb-0">{isAr ? "جارِ تحميل الطلبات..." : "Loading orders..."}</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger rounded-4">{error}</div>
      ) : orders.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body py-5 text-center">
            <h2 className="h4 fw-bold mb-2">
              {isAr ? "لا توجد طلبات خدمات بعد" : "You do not have any freelancer orders yet"}
            </h2>
            <p className="text-muted mb-0">
              {isAr
                ? "ستظهر هنا الطلبات الجديدة بعد الدفع على خدماتك."
                : "New orders will appear here after clients pay for your services."}
            </p>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {orders.map((order) => {
            const service = order.service || {};
            const statusMeta = getStatusMeta(order.status, isAr);
            const actions = getNextActions(order.status);
            const isExpanded = expandedOrderId === order.id;
            const milestoneDraft = milestoneDrafts[order.id] || createMilestoneDraft();
            const milestones = Array.isArray(order.milestones) ? order.milestones : [];
            const disputes = Array.isArray(order.disputes) ? order.disputes : [];
            const refunds = Array.isArray(order.refunds) ? order.refunds : [];
            const milestoneEditingAllowed = canManageMilestones(order);

            return (
              <div className="col-12" key={order.id}>
                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-body p-4">
                    <div className="d-flex flex-wrap justify-content-between gap-3">
                      <div>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                          <span
                            className={`badge ${
                              order.payment_status === "paid"
                                ? "text-bg-success"
                                : order.payment_status === "refunded"
                                  ? "text-bg-danger"
                                  : "text-bg-warning"
                            }`}
                          >
                            {order.payment_status}
                          </span>
                          {order.payment_summary?.escrow_status ? (
                            <span className="badge text-bg-light">
                              {isAr ? "الضمان" : "Escrow"}: {order.payment_summary.escrow_status}
                            </span>
                          ) : null}
                        </div>

                        <h2 className="h4 fw-bold mb-2">
                          {order.title || getFreelancerServiceTitle(service, isAr)}
                        </h2>
                        <div className="small text-muted d-grid gap-2">
                          <div>
                            {isAr ? "العميل:" : "Client:"} {order.client?.name || "-"}
                          </div>
                          <div>
                            {isAr ? "السعر:" : "Price:"}{" "}
                            {formatServiceCurrency(order.price || 0, order.currency || "USD")}
                          </div>
                          <div>
                            {isAr ? "صافي أرباحك:" : "Your net:"}{" "}
                            {formatServiceCurrency(
                              order.payment_summary?.freelancer_amount || 0,
                              order.payment_summary?.currency || order.currency || "USD"
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex flex-column align-items-start align-items-lg-end gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-dark rounded-pill px-4"
                          onClick={() => handleToggleDetails(order.id)}
                        >
                          {detailLoadingId === order.id
                            ? isAr
                              ? "جارِ التحميل..."
                              : "Loading..."
                            : isExpanded
                              ? isAr
                                ? "إخفاء التفاصيل"
                                : "Hide Details"
                              : isAr
                                ? "عرض التفاصيل"
                                : "Show Details"}
                        </button>
                      </div>
                    </div>

                    {order.description ? (
                      <div className="alert alert-light border rounded-4 small mt-3 mb-0">
                        <strong>{isAr ? "ملخص العميل:" : "Client brief:"}</strong> {order.description}
                      </div>
                    ) : null}

                    {actions.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {actions.map((status) => (
                          <button
                            key={status}
                            type="button"
                            className="btn btn-outline-dark rounded-pill"
                            disabled={actionOrderId === order.id}
                            onClick={() => handleStatusUpdate(order.id, status)}
                          >
                            {actionOrderId === order.id
                              ? isAr
                                ? "جارِ الحفظ..."
                                : "Saving..."
                              : status}
                          </button>
                        ))}
                      </div>
                    ) : null}

                    {(order.status === "in_progress" || order.status === "revision_requested") ? (
                      <div className="alert alert-info border rounded-4 mt-3 mb-0">
                        <strong>{isAr ? "تسليم الملفات" : "Deliver Files"}</strong>
                        <div className="mt-2">
                          <textarea
                            rows="2"
                            className="form-control mb-2"
                            placeholder={isAr ? "ملاحظات التسليم (اختياري)" : "Delivery notes (optional)"}
                            value={deliveryDrafts[order.id]?.notes || ""}
                            onChange={(e) =>
                              setDeliveryDrafts((current) => ({
                                ...current,
                                [order.id]: { ...current[order.id], notes: e.target.value },
                              }))
                            }
                          />
                          <input
                            type="file"
                            multiple
                            className="form-control mb-2"
                            onChange={(e) =>
                              setDeliveryDrafts((current) => ({
                                ...current,
                                [order.id]: { ...current[order.id], files: Array.from(e.target.files || []) },
                              }))
                            }
                          />
                          <button
                            type="button"
                            className="btn btn-success rounded-pill"
                            disabled={actionOrderId === order.id || !deliveryDrafts[order.id]?.files?.length}
                            onClick={() => handleDeliver(order.id)}
                          >
                            {actionOrderId === order.id
                              ? isAr
                                ? "جارِ التسليم..."
                                : "Delivering..."
                              : isAr
                                ? "تسليم الملفات"
                                : "Submit Delivery"}
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {order.cancellation_requested_by ? (
                      <div className="alert alert-warning border rounded-4 mt-3 mb-0">
                        <strong>{isAr ? "طلب إلغاء معلق" : "Pending Cancellation Request"}</strong>
                        <div className="small text-muted mt-1">
                          {isAr ? "السبب:" : "Reason:"} {order.cancellation_reason || (isAr ? "لم يتم تحديد سبب" : "No reason provided")}
                        </div>
                        <div className="d-flex gap-2 mt-2">
                          <button
                            type="button"
                            className="btn btn-success btn-sm rounded-pill"
                            disabled={actionOrderId === order.id}
                            onClick={() => handleRespondCancellation(order.id, "approve")}
                          >
                            {actionOrderId === order.id
                              ? isAr
                                ? "جارِ المعالجة..."
                                : "Processing..."
                              : isAr
                                ? "قبول الإلغاء"
                                : "Approve"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm rounded-pill"
                            disabled={actionOrderId === order.id}
                            onClick={() => handleRespondCancellation(order.id, "reject")}
                          >
                            {actionOrderId === order.id
                              ? isAr
                                ? "جارِ المعالجة..."
                                : "Processing..."
                              : isAr
                                ? "رفض الإلغاء"
                                : "Reject"}
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {isExpanded ? (
                      <div className="mt-4 pt-4 border-top">
                        <div className="border rounded-4 p-3 mb-4">
                          <h3 className="h5 fw-bold mb-3">{isAr ? "الرسائل" : "Messages"}</h3>
                          <div
                            className="d-flex flex-column-reverse gap-2 mb-3"
                            style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #e9ecef", borderRadius: 12, padding: 12 }}
                          >
                            {Array.isArray(orderMessages[order.id]) && orderMessages[order.id].length > 0 ? (
                              orderMessages[order.id].map((msg) => (
                                <div
                                  key={msg.id}
                                  className={`d-flex flex-column ${msg.sender_id === order.freelancer?.id ? "align-items-end" : "align-items-start"}`}
                                >
                                  <small className="text-muted mb-1">
                                    {msg.sender?.name || (msg.sender_id === order.freelancer?.id ? "You" : order.client?.name)}
                                    {msg.created_at ? ` - ${new Date(msg.created_at).toLocaleDateString(isAr ? "ar" : "en")}` : ""}
                                  </small>
                                  <div className={`alert ${msg.sender_id === order.freelancer?.id ? "alert-primary" : "alert-light"} mb-0 small`}>
                                    {msg.message}
                                    {msg.attachment_path ? (
                                      <>
                                        <br />
                                        <a
                                          href={`${getStorageBaseUrl()}/storage/service-messages/${order.id}/${msg.attachment_name}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-decoration-underline"
                                        >
                                          {msg.attachment_name}
                                        </a>
                                      </>
                                    ) : null}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-muted small text-center py-3">
                                {isAr ? "لا توجد رسائل بعد" : "No messages yet"}
                              </div>
                            )}
                          </div>
                          <div className="d-grid gap-2">
                            <textarea
                              rows="2"
                              className="form-control"
                              placeholder={isAr ? "اكتب رسالتك..." : "Type your message..."}
                              value={messageTexts[order.id] || ""}
                              onChange={(e) => setMessageTexts((current) => ({ ...current, [order.id]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey && (messageTexts[order.id] || "").trim()) {
                                  e.preventDefault();
                                  handleSendMessage(order.id);
                                }
                              }}
                            />
                            <div className="d-flex gap-2">
                              <input
                                type="file"
                                accept={SERVICE_ORDER_ATTACHMENT_ACCEPT}
                                className="form-control"
                                onChange={(e) => handleMessageFileChange(order.id, e.target.files?.[0] || null, e.target)}
                              />
                              <button
                                type="button"
                                className="btn btn-primary rounded-pill px-4"
                                disabled={
                                  actionOrderId === order.id ||
                                  Boolean(messageFileErrors[order.id]) ||
                                  (!messageTexts[order.id]?.trim() && !messageFiles[order.id])
                                }
                                onClick={() => handleSendMessage(order.id)}
                              >
                                {actionOrderId === order.id
                                  ? isAr
                                    ? "جارِ الإرسال..."
                                    : "Sending..."
                                  : isAr
                                    ? "إرسال"
                                    : "Send"}
                              </button>
                            </div>
                            {messageFileErrors[order.id] ? (
                              <div className="small text-danger">{messageFileErrors[order.id]}</div>
                            ) : null}
                          </div>
                        </div>

                        <div className="row g-4">
                          <div className="col-12 col-lg-6">
                            <div className="border rounded-4 p-3 h-100">
                              <h3 className="h5 fw-bold mb-3">{isAr ? "المعالم" : "Milestones"}</h3>
                              {milestones.length > 0 ? (
                                <div className="d-grid gap-2 mb-3">
                                  {milestones.map((milestone) => (
                                    <div key={milestone.id} className="border rounded-4 p-3">
                                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                                        <strong>{milestone.title}</strong>
                                        <span className="badge text-bg-light">{milestone.status}</span>
                                      </div>
                                      <div className="small text-muted mb-2">
                                        {formatServiceCurrency(milestone.amount || 0, milestone.currency || order.currency || "USD")}
                                      </div>
                                      {milestone.description ? <div className="small mb-2">{milestone.description}</div> : null}
                                      <div className="small text-muted">
                                        {milestone.status === "released"
                                          ? isAr
                                            ? "تمت موافقة العميل وإطلاق المبلغ."
                                            : "Client approved this milestone and released the amount."
                                          : isAr
                                            ? "بانتظار موافقة العميل."
                                            : "Waiting for client approval."}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-muted small mb-3">
                                  {isAr ? "لا توجد معالم بعد." : "No milestones yet."}
                                </div>
                              )}

                              {milestoneEditingAllowed ? (
                                <>
                                  <div className="row g-2">
                                    <div className="col-12">
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder={isAr ? "عنوان المعلم" : "Milestone title"}
                                        value={milestoneDraft.title}
                                        onChange={(event) => updateMilestoneDraft(order.id, "title", event.target.value)}
                                      />
                                    </div>
                                    <div className="col-md-6">
                                      <input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        className="form-control"
                                        placeholder={isAr ? "القيمة" : "Amount"}
                                        value={milestoneDraft.amount}
                                        onChange={(event) => updateMilestoneDraft(order.id, "amount", event.target.value)}
                                      />
                                    </div>
                                    <div className="col-md-6">
                                      <input
                                        type="datetime-local"
                                        className="form-control"
                                        value={milestoneDraft.dueAt}
                                        onChange={(event) => updateMilestoneDraft(order.id, "dueAt", event.target.value)}
                                      />
                                    </div>
                                    <div className="col-12">
                                      <textarea
                                        rows="3"
                                        className="form-control"
                                        placeholder={isAr ? "وصف المعلم" : "Milestone description"}
                                        value={milestoneDraft.description}
                                        onChange={(event) => updateMilestoneDraft(order.id, "description", event.target.value)}
                                      />
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    className="btn btn-primary rounded-pill mt-3"
                                    disabled={actionOrderId === order.id || !milestoneDraft.title.trim() || !milestoneDraft.amount}
                                    onClick={() => handleCreateMilestone(order.id)}
                                  >
                                    {actionOrderId === order.id
                                      ? isAr
                                        ? "جارِ الحفظ..."
                                        : "Saving..."
                                      : isAr
                                        ? "إضافة معلم"
                                        : "Add Milestone"}
                                  </button>
                                </>
                              ) : (
                                <div className="alert alert-light border rounded-4 small mb-0">
                                  {isAr
                                    ? "إضافة المعالم متاحة فقط للطلبات المدفوعة والنشطة، وتتوقف أثناء النزاع أو بعد الإلغاء/الإكمال."
                                    : "Milestones are available only for active paid orders and are locked during disputes or after cancellation/completion."}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="col-12 col-lg-6">
                            <div className="border rounded-4 p-3 h-100">
                              <h3 className="h5 fw-bold mb-3">{isAr ? "الحالة المالية" : "Financial State"}</h3>
                              <div className="small text-muted d-grid gap-2 mb-3">
                                <div>
                                  {isAr ? "إجمالي محجوز:" : "Held in escrow:"}{" "}
                                  {formatServiceCurrency(
                                    (order.payment_summary?.total_amount || 0) - (order.payment_summary?.released_amount || 0),
                                    order.payment_summary?.currency || order.currency || "USD"
                                  )}
                                </div>
                                <div>
                                  {isAr ? "المحرر لك:" : "Released to you:"}{" "}
                                  {formatServiceCurrency(
                                    order.payment_summary?.released_amount || 0,
                                    order.payment_summary?.currency || order.currency || "USD"
                                  )}
                                </div>
                                <div>
                                  {isAr ? "إجمالي المسترد:" : "Refund total:"}{" "}
                                  {formatServiceCurrency(
                                    order.payment_summary?.refund_total || 0,
                                    order.payment_summary?.currency || order.currency || "USD"
                                  )}
                                </div>
                              </div>

                              {refunds.length > 0 ? (
                                <div className="d-grid gap-2 mb-3">
                                  {refunds.map((refund) => (
                                    <div key={refund.id} className="border rounded-4 p-3">
                                      <div className="d-flex flex-wrap justify-content-between gap-2">
                                        <strong>
                                          {formatServiceCurrency(refund.amount || 0, refund.currency || order.currency || "USD")}
                                        </strong>
                                        <span className="badge text-bg-light">{refund.status}</span>
                                      </div>
                                      <div className="small text-muted mt-1">{refund.reason || (isAr ? "بدون سبب." : "No reason.")}</div>
                                    </div>
                                  ))}
                                </div>
                              ) : null}

                              {disputes.length > 0 ? (
                                <div className="d-grid gap-2">
                                  <div className="alert alert-warning py-2 small mb-1">
                                    {isAr
                                      ? "عند فتح نزاع يتم إيقاف إجراءات الطلب مؤقتًا إلى أن يراجع فريق الدعم التفاصيل والرسائل والمرفقات."
                                      : "When a dispute is opened, order actions are paused while support reviews the details, messages, and attachments."}
                                  </div>
                                  {disputes.map((dispute) => (
                                    <div key={dispute.id} className="border rounded-4 p-3">
                                      <div className="d-flex flex-wrap justify-content-between gap-2">
                                        <strong>{dispute.reason}</strong>
                                        <span className="badge text-bg-light">{dispute.status}</span>
                                      </div>
                                      <div className="small text-muted mt-1">{dispute.details || (isAr ? "لا توجد تفاصيل." : "No details.")}</div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-muted small">
                                  {isAr ? "لا توجد نزاعات على هذا الطلب." : "There are no disputes on this order."}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="border rounded-4 p-3">
                              <h3 className="h5 fw-bold mb-3">{isAr ? "مراجعة العميل" : "Client Review"}</h3>
                              {order.review ? (
                                <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                                  <div>
                                    <strong>{order.review.title || (isAr ? "تقييم العميل" : "Client review")}</strong>
                                    <div className="text-muted mt-1">
                                      {order.review.comment || (isAr ? "بدون تعليق إضافي." : "No additional comment.")}
                                    </div>
                                  </div>
                                  <StarRating value={order.review.rating || 0} size={18} showValue />
                                </div>
                              ) : (
                                <div className="text-muted small">
                                  {isAr ? "لا يوجد تقييم من العميل لهذا الطلب بعد." : "The client has not reviewed this order yet."}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
